import { authUrls, userInfoUrls, accountUrls, contactUrls } from '@/configs/endpoints'
import api from '@/plugins/api'
import { setAccessToken } from '@/plugins/tokenStorage'
import { SUCCESS_CODES } from '@/configs/constants'

export default {
  state: () => ({
    userListAccount: [],
    currentPhone: sessionStorage.getItem('phone'),
    // Where the last code went ('sms' | 'telegram'). Kept next to the phone, so a reload on the
    // code screen keeps its wording.
    otpChannel: sessionStorage.getItem('otpChannel'),
    // The phone still owed the notice that codes now come in Telegram, or null. The backend keeps
    // the "seen" flag per phone, so this is the number it is reported back for.
    telegramNoticePhone: null,
    accounts: [],
    ip: undefined,
    qrCodeImage: '',
    qrUuid: '',
    isLoading: false,
    qrCodeForMobile: '',
    isError: false,
    oauthGoogleToken: null,
    oauthGoogleEmail: null,
    emailExist: false,
    contacts: []
  }),
  mutations: {
    SET_DATA_USER(state, data) {
      Object.entries(data).forEach(entry => {
        state[entry[0]] = entry[1]
      })
    },
    // Persist the access token and the accounts list after a login-style response.
    // The current account is derived from the loaded client profile (see the currentAccount getter).
    SET_SESSION(state, data) {
      setAccessToken(data.access_token)
      state.accounts = data.accounts || []
      state.oauthGoogleToken = null
    }
  },
  actions: {
    async getUserListAccount({ commit }) {
      try {
        const response = await api.get(accountUrls.list)
        commit('SET_DATA_USER', { accounts: response.data || [] })
      } catch (error) {
        console.log(error)
      }
    },
    async postChangeAccount({ commit }, payload) {
      try {
        commit('SET_LOAD', true)
        const response = await api.post(accountUrls.change, payload)
        // The change endpoint returns a fresh access token for the selected account.
        if (SUCCESS_CODES.includes(response.code)) setAccessToken(response.data.access_token)
        return response
      } catch (error) {
        console.log(error)
      } finally {
        commit('SET_LOAD', false)
      }
    },
    async postLogoutAnotherAcc({ commit }, uid) {
      try {
        commit('SET_LOAD', true)
        return await api.delete(accountUrls.remove(uid))
      } catch (error) {
        console.log(error)
      } finally {
        commit('SET_LOAD', false)
      }
    },
    async postAddAccount(context, payload) {
      try {
        return await api.post(accountUrls.add, payload)
      } catch (error) {
        console.log(error)
      }
    },
    // Persist a successful login response (tokens + current account) and load the canonical
    // account list, so the header resolves the current account without waiting for a reload.
    applyLogin({ commit, dispatch }, data) {
      commit('SET_SESSION', data)
      dispatch('getUserListAccount')
    },
    async loginWithGoogle({ commit, dispatch }, payload) {
      try {
        const response = await api.post(authUrls.google, { body: payload })
        if (SUCCESS_CODES.includes(response.code)) {
          dispatch('applyLogin', response.data)
        } else {
          // Google account exists but has no linked phone — keep the token to link it via SMS.
          commit('SET_DATA_USER', { oauthGoogleToken: payload.token })
        }
        return response
      } catch (error) {
        console.log(error)
      }
    },
    async connectGoogleOAuth({ commit }, payload) {
      try {
        const response = await api.post(userInfoUrls.oauthGoogle, { body: payload })
        if (SUCCESS_CODES.includes(response.code)) {
          commit('SET_DATA_USER', { oauthGoogleEmail: response.data.email })
        }
        // On error axios already parsed response.data — callers read the error body directly.
        return response
      } catch (error) {
        console.log(error)
      }
    },
    async disconnectGoogleOAuth({ commit }) {
      try {
        const response = await api.delete(userInfoUrls.oauthGoogle)
        if (SUCCESS_CODES.includes(response.code)) {
          commit('SET_DATA_USER', { oauthGoogleEmail: null })
        }

        return response
      } catch (error) {
        console.log(error)
      }
    },
    // Every endpoint that sends a code (`otp/send`, `login`, `contacts/send`) says which channel it
    // went by and whether the phone has seen the Telegram notice. Anything but Telegram keeps the SMS flow.
    rememberOtpChannel({ commit }, { phone, channel, popup_viewed: popupViewed }) {
      const otpChannel = channel || 'sms'
      sessionStorage.setItem('otpChannel', otpChannel)
      // Only an explicit `false` asks for the notice, so a response without the flag never does.
      const telegramNoticePhone = otpChannel === 'telegram' && popupViewed === false ? phone : null
      commit('SET_DATA_USER', { otpChannel, telegramNoticePhone })
    },
    // Closing the Telegram notice without confirming it only hides it: the backend still has it
    // unseen, so the next code sent to this phone brings it back.
    dismissTelegramNotice({ commit }) {
      commit('SET_DATA_USER', { telegramNoticePhone: null })
    },
    // Confirming the notice tells the backend this phone has seen it, so it is not shown again.
    async markTelegramNoticeViewed({ state, commit }) {
      const phone = state.telegramNoticePhone
      if (!phone) return
      commit('SET_DATA_USER', { telegramNoticePhone: null })
      try {
        await api.post(authUrls.popupViewed, { body: { phone, popup_viewed: true } })
      } catch (error) {
        console.log(error)
      }
    },
    async getSMSCode({ state, commit, dispatch }, payload) {
      const body = { ...payload }
      // When linking a Google account, pass its token so the backend ties the phone to it.
      if (state.oauthGoogleToken) body.token = state.oauthGoogleToken
      commit('SET_DATA_USER', { currentPhone: payload.phone })
      sessionStorage.setItem('phone', payload.phone)
      // 200 -> code sent; 400 -> no such phone (fall back to login+password); 429 -> throttled.
      const response = await api.post(authUrls.otpSend, { body })
      // `oauth` says which providers the phone's account is already linked to; linking Google over
      // an existing one replaces its email, which the code screen warns about.
      commit('SET_DATA_USER', { emailExist: !!response.data?.oauth?.google })
      if (SUCCESS_CODES.includes(response.code)) dispatch('rememberOtpChannel', { ...response.data, phone: payload.phone })
      return response
    },
    // Same endpoint, two modes. Response: { qr: <base64 PNG>, token: <uuid> }.
    // Anonymous: poll on `token` until the mobile app authorizes the QR.
    // Authorized: just render the QR for the mobile app to scan (no polling).
    async setQRCode({ commit }, { authorized = false } = {}) {
      try {
        commit('SET_DATA_USER', { isLoading: true, isError: false })
        const response = await api.get(authUrls.qr)
        const image = response.data?.qr ? `data:image/png;base64,${response.data.qr}` : ''
        if (authorized) commit('SET_DATA_USER', { qrCodeForMobile: image })
        else commit('SET_DATA_USER', { qrCodeImage: image, qrUuid: response.data?.token || '' })
      } catch (error) {
        if (!authorized) commit('SET_DATA_USER', { isError: true })
      } finally {
        commit('SET_DATA_USER', { isLoading: false })
      }
    },
    async checkQR(context, uuid) {
      try {
        return await api.get(authUrls.qrPoll(uuid))
      } catch (error) {
        console.log(error)
      }
    },
    async loginQr({ dispatch }, jwt) {
      const response = await api.post(authUrls.loginQr, { body: { token: jwt } })
      if (SUCCESS_CODES.includes(response.code)) dispatch('applyLogin', response.data)
      return response
    },
    // Admin impersonation: exchange the one-time token from the admin panel for a full login
    // response (access/refresh tokens + accounts), identical to a regular login.
    async loginAsAdmin({ dispatch }, token) {
      const response = await api.post(authUrls.admin, { body: { token } })
      if (SUCCESS_CODES.includes(response.code)) {
        dispatch('applyLogin', response.data)
        localStorage.setItem('isAdmin', true)
      }
      return response
    },
    async auth({ state, dispatch }, data) {
      const body = { ...data }
      // Carry the Google token through verification to finish linking the account.
      if (state.oauthGoogleToken) body.token = state.oauthGoogleToken
      // OTP verify: returns access_token + uid + phone of the main account and the accounts list.
      const response = await api.post(authUrls.otpVerify, { body })
      if (SUCCESS_CODES.includes(response.code)) dispatch('applyLogin', response.data)
      return response
    },
    async authLogPassw({ state, dispatch }, data) {
      const phone = state.currentPhone
      // 200 -> code sent (continue to sms-code); 400 -> wrong login/password or code send failed.
      const response = await api.post(authUrls.login, { body: { ...data, phone } })
      if (SUCCESS_CODES.includes(response.code)) dispatch('rememberOtpChannel', { ...response.data, phone })
      return response
    },
    async getContacts({ commit }) {
      try {
        const response = await api.get(contactUrls.list)
        commit('SET_DATA_USER', { contacts: response.data || [] })
      } catch (error) {
        console.log(error)
      }
    },
    async deleteContact({ dispatch }, contactId) {
      try {
        await api.delete(`${contactUrls.list}/${contactId}`)
        await dispatch('getContacts')
        return true
      } catch (error) {
        console.log(error)
        return false
      }
    },
    async sendPhoneVerification({ dispatch }, phone) {
      try {
        const response = await api.post(contactUrls.send, { body: { phone } })
        if (SUCCESS_CODES.includes(response.code)) dispatch('rememberOtpChannel', { ...response.data, phone })
        return response
      } catch (error) {
        console.log(error)
      }
    },
    async verifyPhone(context, payload) {
      try {
        return await api.post(contactUrls.verify, { body: payload })
      } catch (error) {
        console.log(error)
      }
    }
  },
  getters: {
    isAdmin: () => () => JSON.parse(localStorage.getItem('isAdmin')),
    // The current account is the one whose login matches the loaded client profile.
    currentAccount: (state, getters, rootState) =>
      state.accounts?.find(account => account.login === rootState.profile.clientProfile.login),
    otherAccounts: (state, getters, rootState) =>
      state.accounts?.filter(account => account.login !== rootState.profile.clientProfile.login),
    isTelegramOtp: state => state.otpChannel === 'telegram',
    shouldShowTelegramNotice: state => !!state.telegramNoticePhone
  }
}
